import axios from './index';

import type { ResType } from '@/typings/common';

export type UploadAttachmentResult = {
    /** 对象 key，如 admin/2026/06/24/xxx.jpg */
    path: string;
    /** 去掉签名参数后的 S3 地址，上传完成后可立即用于后台预览 */
    objectUrl: string;
};

export const getAttachmentObjectUrl = (uploadUrl: string) => uploadUrl.split('?')[0];

export type PresignedAttachment = {
    /** 对象存储相对路径，如 admin/2026/06/24/xxx.jpg */
    path: string;
    /** 预签名 PUT 地址（临时，仅用于上传） */
    url: string;
};

const MIME_TO_EXTENSION: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'video/mp4': 'mp4',
    'video/quicktime': 'mov',
    'video/webm': 'webm',
    'application/vnd.apple.mpegurl': 'm3u8',
    'application/x-mpegurl': 'm3u8',
};

const EXTENSION_TO_MIME: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    gif: 'image/gif',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    webm: 'video/webm',
    m3u8: 'application/vnd.apple.mpegurl',
};

export const getFileExtension = (file: File): string => {
    const fromName = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : undefined;
    if (fromName && /^[a-z0-9]+$/.test(fromName)) {
        return fromName;
    }

    if (file.type && MIME_TO_EXTENSION[file.type]) {
        return MIME_TO_EXTENSION[file.type];
    }

    return 'bin';
};

export const guessContentType = (extension: string, file?: File): string => {
    if (file?.type) {
        return file.type;
    }
    return EXTENSION_TO_MIME[extension.toLowerCase()] || 'application/octet-stream';
};

export const getAttachmentPresignedUrl = (extension: string) =>
    axios.get<ResType<PresignedAttachment>>('/v1/attachments/presigned', {
        params: { extension },
    });

/**
 * 预签直传：GET presigned → PUT S3
 * - path：写入配置时拼 CDN 前缀
 * - objectUrl：上传后立刻用于后台图片反显（S3 直链，无需落库）
 */
export const uploadAttachmentByPresign = async (file: File): Promise<UploadAttachmentResult> => {
    const extension = getFileExtension(file);
    const { data } = await getAttachmentPresignedUrl(extension);

    if (data.code !== 200) {
        throw new Error(data.msg || 'Failed to get presigned url');
    }

    const uploadUrl = data.data?.url;
    const path = data.data?.path;

    if (!uploadUrl || !path) {
        throw new Error('Presigned response missing url or path');
    }

    const contentType = guessContentType(extension, file);

    const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
            'Content-Type': contentType,
        },
    });

    if (!response.ok) {
        const detail = await response.text().catch(() => '');
        throw new Error(detail || `Upload failed (${response.status})`);
    }

    const normalizedPath = path.replace(/^\//, '');

    return {
        path: normalizedPath,
        objectUrl: getAttachmentObjectUrl(uploadUrl),
    };
};
